import type { SocketTelemetryPayload } from "./types";

const TRACE_ID_HEX_LENGTH = 32;
const SPAN_ID_HEX_LENGTH = 16;
const ALLOWED_SYNTHETIC_ENVIRONMENTS = new Set(["stage", "staging", "sandbox", "nonprod", "test"]);
const ALLOWED_SYNTHETIC_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "stage.phoenix.edu",
  "phoebe-stage.phoenix.edu",
]);
const ALLOWED_SYNTHETIC_HOST_SUFFIXES = [".sandbox.uopx.io", ".st.uopx.io"];
const SYNTHETIC_SLUG_RE = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;

type PhoebeWindow = Window & {
  newrelic?: {
    addPageAction?: (name: string, attributes?: Record<string, unknown>) => void;
    setCustomAttribute?: (name: string, value: string | number | boolean) => void;
  };
  __PHOEBE_TELEMETRY_SESSION_MARKER__?: string;
  __PHOEBE_WIDGET_TELEMETRY__?: Array<{ event_name: string; attributes: Record<string, unknown> }>;
};

const WIDGET_VERSION = "0.1.0";

function randomHex(length: number): string {
  const bytes = new Uint8Array(length / 2);
  globalThis.crypto?.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function createTraceparent(): string {
  return `00-${randomHex(TRACE_ID_HEX_LENGTH)}-${randomHex(SPAN_ID_HEX_LENGTH)}-01`;
}

function getSessionMarker(win: PhoebeWindow): string {
  if (!win.__PHOEBE_TELEMETRY_SESSION_MARKER__) {
    win.__PHOEBE_TELEMETRY_SESSION_MARKER__ = `phoebe-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return win.__PHOEBE_TELEMETRY_SESSION_MARKER__;
}

function sanitizeSyntheticSlug(value: string | null | undefined, maxLength: number): string | undefined {
  const normalized = value?.trim().toLowerCase();
  if (!normalized || normalized.length > maxLength || !SYNTHETIC_SLUG_RE.test(normalized)) return undefined;
  return normalized;
}

function allowsSyntheticTelemetry(hostname: string | undefined): boolean {
  const normalized = hostname?.trim().toLowerCase();
  if (!normalized) return false;
  return (
    ALLOWED_SYNTHETIC_HOSTS.has(normalized) ||
    ALLOWED_SYNTHETIC_HOST_SUFFIXES.some((suffix) => normalized.endsWith(suffix))
  );
}

function syntheticTelemetryFromLocation(win: PhoebeWindow): Pick<
  SocketTelemetryPayload,
  "synthetic" | "synthetic_environment" | "synthetic_scenario"
> {
  if (!allowsSyntheticTelemetry(win.location?.hostname)) return {};

  const params = new URLSearchParams(win.location?.search ?? "");
  const environment = sanitizeSyntheticSlug(
    params.get("phoebe_synthetic") ?? params.get("phoebeSynthetic"),
    32,
  );
  if (!environment || !ALLOWED_SYNTHETIC_ENVIRONMENTS.has(environment)) return {};

  const scenario = sanitizeSyntheticSlug(
    params.get("phoebe_synthetic_scenario") ?? params.get("phoebeSyntheticScenario"),
    64,
  );
  return {
    synthetic: true,
    synthetic_environment: environment,
    ...(scenario ? { synthetic_scenario: scenario } : {}),
  };
}

export function buildSocketTelemetryPayload(clientMessageId: string): SocketTelemetryPayload | undefined {
  if (typeof window === "undefined" || !globalThis.crypto?.getRandomValues) return undefined;

  const win = window as PhoebeWindow;
  const syntheticTelemetry = syntheticTelemetryFromLocation(win);
  return {
    traceparent: createTraceparent(),
    client_message_id: clientMessageId,
    source: "browser",
    page_origin: win.location?.origin,
    page_host: win.location?.hostname,
    session_marker: getSessionMarker(win),
    ...syntheticTelemetry,
  };
}

export function telemetryUrlFromBackendUrl(backendUrl: string): string {
  return `${backendUrl.replace(/\/$/, "")}/v1/telemetry/widget`;
}

export function sendWidgetTelemetry({
  backendUrl,
  eventName,
  clientMessageId,
  traceId,
  traceparent,
  durationMs,
  firstResponseMs,
  suggestionId,
  suggestionCategory,
  suggestionPosition,
}: {
  backendUrl: string;
  eventName:
    | "chat_opened"
    | "chat_turn_started"
    | "chat_turn_completed"
    | "handoff_requested"
    | "guest_session_bootstrap_succeeded"
    | "suggestion_chip_impression"
    | "suggestion_chip_selected"
    | "widget_error";
  clientMessageId?: string;
  traceId?: string;
  traceparent?: string;
  durationMs?: number;
  firstResponseMs?: number;
  suggestionId?: string;
  suggestionCategory?: string;
  suggestionPosition?: number;
}): void {
  if (typeof window === "undefined") return;

  const win = window as PhoebeWindow;
  const syntheticTelemetry = syntheticTelemetryFromLocation(win);
  const attributes = {
    ...(clientMessageId ? { client_message_id: clientMessageId } : {}),
    ...(traceId ? { trace_id: traceId } : {}),
    ...(traceparent ? { traceparent } : {}),
    ...(typeof durationMs === "number" ? { duration_ms: durationMs } : {}),
    ...(typeof firstResponseMs === "number" ? { first_response_ms: firstResponseMs } : {}),
    ...(suggestionId ? { suggestion_id: suggestionId } : {}),
    ...(suggestionCategory ? { suggestion_category: suggestionCategory } : {}),
    ...(typeof suggestionPosition === "number" ? { suggestion_position: suggestionPosition } : {}),
    page_origin: win.location?.origin,
    page_host: win.location?.hostname,
    session_marker: getSessionMarker(win),
    widget_version: WIDGET_VERSION,
    ...syntheticTelemetry,
  };
  win.__PHOEBE_WIDGET_TELEMETRY__?.push({ event_name: eventName, attributes });

  const body = JSON.stringify({ event_name: eventName, ...attributes });
  const url = telemetryUrlFromBackendUrl(backendUrl);
  if (navigator.sendBeacon) {
    const sent = navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    if (sent) return;
  }

  void fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
    credentials: "include",
  }).catch(() => undefined);
}

export function recordSocketTurnTelemetry({
  clientMessageId,
  traceId,
}: {
  clientMessageId: string;
  traceId?: string;
}): void {
  if (typeof window === "undefined") return;

  const newrelic = (window as PhoebeWindow).newrelic;
  (window as PhoebeWindow).__PHOEBE_WIDGET_TELEMETRY__?.push({
    event_name: "host_newrelic_mirror",
    attributes: {
      client_message_id: clientMessageId,
      ...(traceId ? { trace_id: traceId } : {}),
    },
  });
  newrelic?.setCustomAttribute?.("phoebe.client_message_id", clientMessageId);
  if (traceId) newrelic?.setCustomAttribute?.("phoebe.trace_id", traceId);
  newrelic?.addPageAction?.("phoebe_chat_turn", {
    client_message_id: clientMessageId,
    ...(traceId ? { trace_id: traceId } : {}),
  });
}
